import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, interval, of} from 'rxjs';
import {catchError, distinctUntilChanged, filter, switchMap} from 'rxjs/operators';

import {environment} from '@environments/environment';
import {ActionCode, EffectivePermissions} from '@app/models';
import {AccountService} from '@app/services/account.service';

/** FE-ROL-07: intervalo de refresco — proporcional al riesgo (RQNF05), no
 * tiempo real. Ver design.md §16 de specs/roles-sprint-2.
 * Exportada para que el spec pueda avanzar el reloj con `tick()` sin
 * hardcodear el número. */
export const AUTH_VERSION_POLL_INTERVAL_MS = 5 * 60 * 1000;

interface EffectivePermissionsResponse {
  permissions?: EffectivePermissions;
  auth_version?: number;
}

/**
 * Resuelve los permisos efectivos del usuario logueado a respuestas booleanas
 * sobre (reference_code, action). Refetchea GET /authz/me/permissions/ cuando
 * cambia el usuario (login/logout/refresh) o el tenant activo, y además hace
 * polling periódico comparando `auth_version` (FE-ROL-07/FM-ROL-14) para
 * reflejar cambios de permisos hechos por un admin mientras la sesión sigue
 * abierta — sin necesidad de logout manual.
 */
@Injectable({providedIn: 'root'})
export class PermissionsService {
  private readonly permissionsSubject = new BehaviorSubject<EffectivePermissions>({});
  public readonly permissions: Observable<EffectivePermissions> = this.permissionsSubject.asObservable();

  // ¿ya cargaron los permisos del usuario actual? El guard lo espera para no
  // bloquear con el snapshot vacío durante la carga inicial (URL directa/refresh).
  private readonly readySubject = new BehaviorSubject<boolean>(false);
  public readonly ready$: Observable<boolean> = this.readySubject.asObservable();

  private lastKnownAuthVersion: number | undefined;

  constructor(
    private accountService: AccountService,
    private http: HttpClient,
  ) {
    // Refetchea cuando cambia el id del usuario (login/logout/switch) O el
    // tenant activo (FE-ROL-06/FM-ROL-12) — mismo criterio de "org activa"
    // que ya usa AccountService.currentOrgIsMerchandising(): connectedToOrg
    // tiene prioridad, si no hay, cae a user.organization.
    this.accountService.user.pipe(
      distinctUntilChanged((prev, curr) => {
        const sameUser = (prev?.id ?? null) === (curr?.id ?? null);
        const prevOrgId = (prev?.connectedToOrg ?? prev?.organization)?.id ?? null;
        const currOrgId = (curr?.connectedToOrg ?? curr?.organization)?.id ?? null;
        return sameUser && prevOrgId === currOrgId;
      })
    ).subscribe((user) => {
      if (!user) {
        // Sin usuario no hay nada que cargar (AuthGuard ya redirige al login).
        this.lastKnownAuthVersion = undefined;
        this.permissionsSubject.next({});
        this.readySubject.next(true);
        return;
      }
      this.readySubject.next(false); // empieza la carga del usuario actual
      this.fetchEffectivePermissionsResponse().subscribe(response => {
        // Carga inicial (login/switch de usuario u organización): sin
        // snapshot previo del usuario actual que valga la pena conservar,
        // así que un error acá sigue vaciando permisos (fail-closed).
        this.lastKnownAuthVersion = response?.auth_version;
        this.permissionsSubject.next(response?.permissions ?? {});
        this.readySubject.next(true);
      });
    });

    // FE-ROL-07: polling — mientras haya sesión activa, vuelve a preguntar
    // cada N minutos y solo actualiza el snapshot si auth_version cambió
    // (evita re-renders innecesarios cuando nada cambió del lado del rol).
    interval(AUTH_VERSION_POLL_INTERVAL_MS).pipe(
      filter(() => !!this.accountService.userValue),
      switchMap(() => this.fetchEffectivePermissionsResponse()),
    ).subscribe(response => {
      // W1 (auditoría roles-sprint-2, ciclo 2): antes, un error transitorio
      // del poll (network blip, 5xx) devolvía {permissions:{}} SIN
      // auth_version — `undefined !== lastKnownAuthVersion` se leía como
      // "cambió", pisando el snapshot en plena sesión con menús vacíos
      // hasta el próximo poll exitoso (hasta 5 min). Ahora un error del
      // poll no emite nada — conserva lo último bueno, reintenta solo.
      if (!response) {
        return;
      }
      if (response.auth_version !== this.lastKnownAuthVersion) {
        this.lastKnownAuthVersion = response.auth_version;
        this.permissionsSubject.next(response.permissions ?? {});
      }
    });
  }

  /** ¿El usuario tiene la acción permitida para el código? */
  hasPermission(referenceCode: string, action: ActionCode): boolean {
    const allowed = this.snapshot()[this.prefixedCode(referenceCode)];
    return Array.isArray(allowed) && allowed.includes(action);
  }

  /** ¿El usuario puede acceder a la opción o sección? (al menos una acción permitida) */
  hasAccess(referenceCode: string): boolean {
    const code = this.prefixedCode(referenceCode);
    const snap = this.snapshot();
    const allowed = snap[code];
    if (Array.isArray(allowed) && allowed.length > 0) {
      return true;
    }
    if (code === 'W-ACTIVIDADES') {
      return Object.keys(snap).some(k => k.startsWith('W-ACT.') && (snap[k]?.length ?? 0) > 0);
    }
    if (code === 'W-PARAMETRIZACION') {
      return Object.keys(snap).some(k => k.startsWith('W-PAR.') && (snap[k]?.length ?? 0) > 0);
    }
    return false;
  }

  /** Snapshot sincrónico para guards y directivas. */
  snapshot(): EffectivePermissions {
    return this.permissionsSubject.value;
  }

  /**
   * El backend prefija las claves por canal (`W-` web, `M-` móvil). Si llega el code
   * limpio le antepone `W-`; si ya viene prefijado, lo respeta.
   */
  private prefixedCode(referenceCode: string): string {
    if (referenceCode.startsWith('W-') || referenceCode.startsWith('M-')) {
      return referenceCode;
    }
    return `W-${referenceCode}`;
  }

  /** `null` distingue un error de transporte de una respuesta 200 legítima
   * con permisos vacíos — el poll (W1) necesita esa distinción para no
   * confundir "el usuario efectivamente no tiene permisos" con "la request
   * falló", que son casos muy distintos. */
  private fetchEffectivePermissionsResponse(): Observable<EffectivePermissionsResponse | null> {
    const url = `${environment.api.auth}/${this.accountService.language}/api/v1/authz/me/permissions/`;
    return this.http.get<EffectivePermissionsResponse>(url).pipe(
      catchError(() => of<EffectivePermissionsResponse | null>(null)),
    );
  }
}
